import React, { useState } from "react";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import axios from "axios";
import { useParams } from "react-router-dom";
import baseURL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Stripe currency limits
const currencyMinimums = {
  INR: 50,
  USD: 1,
  EUR: 1,
  GBP: 1,
  JPY: 100,
  AUD: 1,
  CAD: 1,
  CNY: 5,
  SGD: 1,
  NZD: 1,
  CHF: 1,
  ZAR: 10,
  AED: 2,
  SAR: 2,
  RUB: 50,
  KRW: 1000,
  BRL: 5,
  MXN: 20,
  HKD: 5,
  MYR: 5,
  THB: 20,
};

const currencyMaximums = {
  INR: 4999999,
  USD: 999999,
  EUR: 999999,
  GBP: 999999,
  JPY: 99999999,
  AUD: 999999,
  CAD: 999999,
  CNY: 999999,
  SGD: 999999,
  NZD: 999999,
  CHF: 999999,
  ZAR: 999999,
  AED: 3672823,
  SAR: 999999,
  RUB: 999999,
  KRW: 1199999999,
  BRL: 999999,
  MXN: 999999,
  HKD: 999999,
  MYR: 999999,
  THB: 999999,
};

const currencies = Object.keys(currencyMinimums);

const WalletForm = () => {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [loading, setLoading] = useState(false);
  const { id } = useParams();

  const min = currencyMinimums[currency];
  const max = currencyMaximums[currency];

  const isAmountInvalid = () => {
    const amt = parseFloat(amount);
    return isNaN(amt) || amt < min || amt > max;
  };

  const getHelperText = () => {
    if (amount === "") return "";
    const amt = parseFloat(amount);
    if (isNaN(amt)) return "❌ Enter a valid numeric amount.";
    if (amt < min) return `❌ Minimum amount for ${currency} is ${min}.`;
    if (amt > max) return `❌ Maximum amount for ${currency} is ${max}.`;
    return `✅ Allowed range: ${min} - ${max} ${currency}`;
  };

  const handleTopUp = async () => {
    const amt = parseFloat(amount);

    if (isNaN(amt) || amt < min || amt > max) {
      toast.error("⚠️ Please enter an amount within the valid range.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${baseURL}/api/wallet/topup`, {
        amount: amt,
        userId: id,
        currency,
      });

      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("❌ Stripe Checkout session failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error("❌ Failed to initiate top-up.");
    }
    setLoading(false);
  };

  return (
    <Card sx={{ p: 3, maxWidth: 500, margin: "auto", mt: 5, boxShadow: 3 }}>
      <ToastContainer position="top-right" autoClose={4000} />
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Add Wallet Balance
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Currency</InputLabel>
          <Select
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setAmount(""); // Reset amount to avoid invalid range
            }}
            label="Currency"
          >
            {currencies.map((cur) => (
              <MenuItem key={cur} value={cur}>
                {cur}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={`Amount (${currency})`}
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
          helperText={getHelperText()}
          error={isAmountInvalid()}
        />

        <Button
          variant="contained"
          fullWidth
          disabled={loading || isAmountInvalid()}
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleTopUp}
          sx={{
            backgroundColor: "#C30B12",
            color: "#fff",
            fontWeight: 600,
            py: 1.2,
            "&:hover": { backgroundColor: "#a00a0f" },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Add Amount"
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default WalletForm;
